import { DialogName, ModalName } from 'app/enums';
import { modalsReducers } from 'app/store/modal/slice';
import { currentTournamentReducers } from 'modules/tournaments/store/current-tournament/slice';
import { useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTeamDetails } from './use-team-details';
import { TOURNAMENT_ID_QUERY_KEY } from 'app/constants';
import { TournamentEntity } from 'app/interfaces/entity';
import { currentTournamentSelector } from '../store/current-tournament/selectors';
import { WindowFocusDetectorContext } from 'app/providers';

interface UseTournamentModalHook {
  openTournamentModal: () => void;
  closeTournamentModal: () => void;
  setTournamentIdQueryParam: (identifier: string) => void;
  removeTournamentIdQueryParam: () => void;
  openWinningAnimationModal: () => void;
}

export const useTournamentModal = (): UseTournamentModalHook => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { focused } = useContext(WindowFocusDetectorContext);
  const { isWinningAnimationAvailable } = useTeamDetails();
  const tournament: TournamentEntity = useSelector(currentTournamentSelector.tournament);

  const openWinningAnimationModal = useCallback(() => {
    if (!tournament?.id || !isWinningAnimationAvailable || !focused) {
      return;
    }

    dispatch(modalsReducers.closeModal(ModalName.Tournament));
    dispatch(modalsReducers.openModal({ name: DialogName.WinningAnimation }));
  }, [dispatch, focused, isWinningAnimationAvailable, tournament?.id]);

  const openTournamentModal = useCallback(() => {
    if (!tournament?.id) {
      return;
    }

    if (isWinningAnimationAvailable) {
      openWinningAnimationModal();
    } else {
      dispatch(modalsReducers.openModal({ name: ModalName.Tournament }));
    }
  }, [dispatch, isWinningAnimationAvailable, openWinningAnimationModal, tournament?.id]);

  const closeTournamentModal = useCallback(() => {
    dispatch(modalsReducers.closeModal(ModalName.Tournament));

    dispatch(currentTournamentReducers.clearTournament());
    navigate(pathname);
  }, [dispatch, navigate, pathname]);

  const setTournamentIdQueryParam = useCallback(
    (identifier: string) => {
      navigate({
        pathname: pathname,
        search: `?${TOURNAMENT_ID_QUERY_KEY}=${identifier}`,
      });
    },
    [navigate, pathname]
  );

  const removeTournamentIdQueryParam = useCallback(() => navigate(pathname), [navigate, pathname]);

  return {
    openTournamentModal,
    closeTournamentModal,
    setTournamentIdQueryParam,
    removeTournamentIdQueryParam,
    openWinningAnimationModal,
  };
};
